<script>
/**
 * @component ProceduresList
 * @description UC-REG-160 — Bandeja del staff: "Gestiones de trámites".
 *              Filtros arriba (search + estado + tipo) tipo AuthorizationList.
 *              Click en fila abre modal detalle con chat de ida y vuelta +
 *              textarea para responder + botón "Cerrar caso".
 *
 * @example <ProceduresList />
 */
import procedureService, { procedureTypeService } from "@/services/procedure";
import doctorService from "@/services/doctor";
import {
  formatProcedureAppointment,
  findProcedureAppointmentDoctor,
  doctorInstitutions,
  resolveInstitutionAppointment,
  canCancelProcedureAppointment,
  procedureCancelErrorMessage,
  PROCEDURE_CANCEL_DIALOG,
} from "@/services/procedure-appointment";
import {
  isProcedureAppointmentByInstitution,
  procedureAppointmentDoctorUsername,
  isProcedurePreferredDoctorEnabled,
} from "@/services/config";
import {
  PREFERRED_DOCTOR_MAX,
  childrenOf,
  fileKindLabel,
  filterOptionGroups,
  normalizePreferredDoctor,
  priorityText,
  requestText,
  resolveSelectedType,
  topLevelTypes,
  typeLabel,
} from "@/services/procedure-types-view";
import { toApiUTC } from "@/services/datetime";
import { format } from "date-fns";
import { mapGetters } from "vuex";

/**
 * @description UC-REG-253 — Estados de Appointment que cuentan como "turno
 *              vigente" del trámite (mismo criterio que el backend).
 */
const ACTIVE_APPOINTMENT_STATUSES = ["pending", "admitted", "started"];

export default {
  name: "ProceduresList",

  data() {
    return {
      procedures: [],
      types: [],
      loading: false,
      searchInput: "",
      statusFilter: "",
      typeFilter: "",
      searchDebounce: null,

      detail: {
        visible: false,
        procedure: null,
        newMessage: "",
        // Adjuntos que van en el próximo mensaje del staff. Se envían
        // junto con el texto al hacer click en Enviar respuesta.
        newFiles: [],
        // Canal por el que se notifica al submitter cuando el staff responde.
        // Default 'email' (más confiable). El operador puede cambiarlo por
        // respuesta — evita spamear ambos canales al paciente.
        notifyChannel: "email",
        sending: false,
        closing: false,
        // UC-REG-253: turno que se carga junto con la respuesta cuando el
        // tipo lo requiere (o para reprogramar). `startsAt` es un Date
        // local (el-date-picker); se manda como ISO UTC.
        appointment: {
          startsAt: null,
          doctor: "",
          institution: "",
          reschedule: false,
        },
      },

      // UC-REG-253: médicos de la organización para el select del turno.
      // Se cargan una sola vez, al abrir el primer trámite que lo necesita.
      doctors: [],
      doctorsLoaded: false,

      // UC-REG-262 (Campana): el turno se elige por institución; el médico
      // es un prestador ficticio que no se muestra. Flag de build.
      appointmentByInstitution: isProcedureAppointmentByInstitution,
      procedureDoctorUsername: procedureAppointmentDoctorUsername,

      // UC-REG-298 (Campana): "Médico de preferencia" en lugar de la
      // descripción en el alta del staff. Flag de build.
      preferredDoctorMode: isProcedurePreferredDoctorEnabled,
      preferredDoctorMax: PREFERRED_DOCTOR_MAX,

      // Modal "Nuevo trámite" — el staff carga en nombre de un paciente.
      // Search de pacientes: `patientQuery` con debounce dispara
      // `patientService.get({ $text: { $search } })`. El staff elige uno del
      // dropdown y ese Patient._id viaja al backend como submitterPatientId.
      newProc: {
        visible: false,
        saving: false,
        patientQuery: "",
        patientResults: [],
        patientSearching: false,
        patientDebounce: null,
        // Canal por el que se le avisa al paciente que hay un trámite
        // nuevo creado a su nombre. Default email (más confiable).
        notifyChannel: "email",
        form: {
          submitterPatient: null,
          procedureType: "",
          procedureSubtype: "",
          description: "",
          preferredDoctor: "",
          files: [],
        },
      },
    };
  },

  computed: {
    ...mapGetters(["user"]),

    /**
     * @description UC-REG-297 — Opciones del filtro de tipo agrupadas por
     *   tipología (optgroup) — filtrar por la tipología incluye subtipos.
     * @returns {Array<{label:string|null,options:Array}>}
     */
    typeFilterGroups() {
      return filterOptionGroups(this.types);
    },

    /**
     * @description UC-REG-297 — Tipologías del alta del staff.
     * @returns {Array<Object>}
     */
    newProcTopTypes() {
      return topLevelTypes(this.types);
    },

    /**
     * @description UC-REG-297 — Subtipos de la tipología elegida en el alta.
     * @returns {Array<Object>}
     */
    newProcSubtypes() {
      return childrenOf(this.types, this.newProc.form.procedureType);
    },

    /**
     * @description UC-REG-253 — El trámite abierto admite turno: el tipo lo
     *              requiere y el solicitante es paciente (los turnos son de
     *              pacientes, no de prestadores).
     * @returns {boolean}
     */
    detailAllowsAppointment() {
      const p = this.detail.procedure;
      return Boolean(
        p &&
          p.submitterType === "patient" &&
          p.procedureType &&
          p.procedureType.requiresAppointment === true
      );
    },

    /**
     * @description Turno vigente del trámite abierto (pending/admitted/
     *              started) o null.
     * @returns {Object|null}
     */
    detailActiveAppointment() {
      const a = this.detail.procedure && this.detail.procedure.appointment;
      if (!a || !ACTIVE_APPOINTMENT_STATUSES.includes(a.status)) return null;
      return a;
    },

    /**
     * @description UC-REG-300 — El turno vigente del trámite abierto se
     *              puede cancelar sin reemplazarlo (flag de Campana).
     * @returns {boolean}
     */
    canCancelDetailAppointment() {
      const a = this.detailActiveAppointment;
      const p = this.detail.procedure;
      if (!a || !p || p.status === "closed") return false;
      return canCancelProcedureAppointment({ ...a, procedure: p._id });
    },

    /**
     * @description La próxima respuesta DEBE traer turno: el tipo lo exige
     *              y todavía no hay uno vigente.
     * @returns {boolean}
     */
    appointmentRequired() {
      return this.detailAllowsAppointment && !this.detailActiveAppointment;
    },

    /**
     * @description El form de turno está visible: obligatorio, o el operador
     *              eligió reprogramar.
     * @returns {boolean}
     */
    appointmentFormVisible() {
      return (
        this.detailAllowsAppointment &&
        (this.appointmentRequired || this.detail.appointment.reschedule)
      );
    },

    /**
     * @description Sedes del médico elegido en el form de turno. Si tiene
     *              una sola, el backend la toma solo y no se muestra select.
     * @returns {Array<{_id:string,name:string}>}
     */
    selectedDoctorInstitutions() {
      const id = this.detail.appointment.doctor;
      const doc = this.doctors.find((d) => d._id === id);
      const list = (doc && doc.doctor && doc.doctor.institutions) || [];
      return list.filter((i) => i && i._id);
    },

    /**
     * @description UC-REG-262 — Instituciones del prestador ficticio de
     *              turnos de trámite (opciones del select "Institución").
     *              Vacío si el modo está apagado o el prestador no existe.
     * @returns {Array<{_id:string,name:string}>}
     */
    procedureInstitutions() {
      if (!this.appointmentByInstitution) return [];
      return doctorInstitutions(
        findProcedureAppointmentDoctor(
          this.doctors,
          this.procedureDoctorUsername
        )
      );
    },

    /**
     * @description Opciones para el picker: no permite días pasados.
     * @returns {Object}
     */
    appointmentPickerOptions() {
      return {
        disabledDate(date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date < today;
        },
      };
    },
  },

  created() {
    this.load();
    this.loadTypes();
  },

  watch: {
    statusFilter() {
      this.load();
    },
    typeFilter() {
      this.load();
    },
    searchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.load(), 350);
    },
  },

  methods: {
    priorityText,
    async load() {
      this.loading = true;
      try {
        const params = {};
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.typeFilter) params.procedureType = this.typeFilter;
        if (this.searchInput.trim()) params.search = this.searchInput.trim();
        this.procedures = await procedureService.list(params);
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo cargar la bandeja"
        );
      } finally {
        this.loading = false;
      }
    },

    async loadTypes() {
      try {
        this.types = await procedureTypeService.list();
      } catch (err) {
        // Non-fatal: el filtro por tipo queda vacío pero la bandeja funciona.
      }
    },

    clearFilters() {
      this.searchInput = "";
      this.statusFilter = "";
      this.typeFilter = "";
    },

    /**
     * @description Abre el modal "Nuevo trámite" para que el staff cargue
     *              uno en nombre de un paciente (misma capacidad que el
     *              portal del paciente).
     */
    openNew() {
      if (this.types.length === 0) {
        this.$message.warning(
          "No hay tipos de trámite activos. Cargá al menos uno desde Tipos de trámite."
        );
        return;
      }
      this.newProc = {
        visible: true,
        saving: false,
        patientQuery: "",
        patientResults: [],
        patientSearching: false,
        patientDebounce: null,
        notifyChannel: "email",
        form: {
          submitterPatient: null,
          procedureType: "",
          procedureSubtype: "",
          description: "",
          preferredDoctor: "",
          files: [],
        },
      };
    },
    closeNew() {
      this.newProc.visible = false;
    },

    /**
     * @description UC-REG-297 — Al cambiar la tipología del alta se resetea
     *   el subtipo.
     */
    onNewProcTypeChange() {
      this.newProc.form.procedureSubtype = "";
    },

    onPatientQueryInput() {
      if (this.newProc.patientDebounce) {
        clearTimeout(this.newProc.patientDebounce);
      }
      this.newProc.patientDebounce = setTimeout(
        () => this.searchPatients(),
        300
      );
    },
    async searchPatients() {
      const q = this.newProc.patientQuery.trim();
      if (q.length < 2) {
        this.newProc.patientResults = [];
        return;
      }
      this.newProc.patientSearching = true;
      try {
        // Endpoint dedicado del módulo: /procedures/patients-search.
        // No requiere permiso `patient.query` (que no todos los roles
        // staff tienen) y devuelve identidad completa (email/phone) que
        // usamos para armar la notificación al crear.
        const res = await procedureService.searchPatients(q);
        this.newProc.patientResults = Array.isArray(res) ? res : [];
      } catch (err) {
        this.newProc.patientResults = [];
      } finally {
        this.newProc.patientSearching = false;
      }
    },
    pickPatient(p) {
      this.newProc.form.submitterPatient = p;
      this.newProc.patientResults = [];
      this.newProc.patientQuery = this.formatPatientLabel(p);
    },
    /**
     * @description Formato del chip que muestra el paciente seleccionado.
     *              Nombre + DNI. Los tokens vienen del endpoint dedicado
     *              `/procedures/patients-search`.
     */
    formatPatientLabel(p) {
      if (!p) return "";
      const name = [p.firstName, p.lastName].filter(Boolean).join(" ");
      const dni = p.governmentId?.number
        ? ` — DNI ${p.governmentId.number}`
        : "";
      return `${name}${dni}`;
    },
    onNewProcFiles(event) {
      const picked = Array.from(event.target.files || []);
      picked.forEach((f) => {
        if (f.size > 15 * 1024 * 1024) {
          this.$message.warning(`"${f.name}" supera los 15MB, no se adjunta`);
          return;
        }
        if (this.newProc.form.files.length >= 10) {
          this.$message.warning("Máximo 10 adjuntos");
          return;
        }
        this.newProc.form.files.push(f);
      });
      event.target.value = "";
    },
    removeNewProcFile(index) {
      this.newProc.form.files.splice(index, 1);
    },
    /**
     * @description Envía el POST /procedures armado por el admin. El backend
     *              resuelve el User del paciente y usa esa referencia como
     *              submitter (submitterType='patient').
     */
    async submitNewProc() {
      const {
        submitterPatient,
        procedureType,
        procedureSubtype,
        description,
        preferredDoctor,
        files,
      } = this.newProc.form;
      if (!submitterPatient) {
        this.$message.warning("Elegí un paciente");
        return;
      }
      const resolved = resolveSelectedType({
        types: this.types,
        typeId: procedureType,
        subtypeId: procedureSubtype,
      });
      if (!resolved.ok) {
        this.$message.warning(resolved.error);
        return;
      }
      if (!this.preferredDoctorMode && !description.trim()) {
        this.$message.warning("Escribí una descripción");
        return;
      }
      this.newProc.saving = true;
      try {
        const form = new FormData();
        form.append("submitterPatientId", submitterPatient.patientId);
        form.append("procedureType", resolved.typeId);
        form.append("description", description.trim());
        const doctor = normalizePreferredDoctor(preferredDoctor);
        if (doctor) form.append("preferredDoctor", doctor);
        form.append("notify", this.newProc.notifyChannel);
        files.forEach((f) => form.append("files", f, f.name));
        await procedureService.create(form);
        this.$message.success("Trámite creado");
        this.newProc.visible = false;
        await this.load();
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo crear el trámite"
        );
      } finally {
        this.newProc.saving = false;
      }
    },

    // ═══ Chat ═══
    onPickChatFiles(event) {
      const picked = Array.from(event.target.files || []);
      picked.forEach((f) => {
        if (f.size > 15 * 1024 * 1024) {
          this.$message.warning(`"${f.name}" supera los 15MB, no se adjunta`);
          return;
        }
        if (this.detail.newFiles.length >= 10) {
          this.$message.warning("Máximo 10 adjuntos por mensaje");
          return;
        }
        this.detail.newFiles.push(f);
      });
      event.target.value = "";
    },
    removeChatFile(index) {
      this.detail.newFiles.splice(index, 1);
    },
    formatFileSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    },

    async openDetail(row) {
      try {
        const full = await procedureService.getById(row._id);
        this.detail = {
          visible: true,
          procedure: full,
          newMessage: "",
          newFiles: [],
          notifyChannel: "email",
          sending: false,
          closing: false,
          appointment: this.emptyAppointmentForm(),
        };
        if (this.detailAllowsAppointment) this.loadDoctors();
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        this.$message.error("No se pudo abrir el trámite");
      }
    },
    close() {
      this.detail.visible = false;
    },

    // ═══ UC-REG-253: turno asociado ═══

    /**
     * @description Form vacío del turno. Con un solo médico en la org lo
     *              preselecciona (menos clicks para el operador).
     * @returns {{startsAt:Date|null,doctor:string,institution:string,reschedule:boolean}}
     */
    emptyAppointmentForm() {
      return {
        startsAt: null,
        doctor: this.doctors.length === 1 ? this.doctors[0]._id : "",
        institution: "",
        reschedule: false,
      };
    },

    /**
     * @description Carga los médicos activos de la organización (con sedes
     *              y especialidades populadas) para el select del turno.
     *              Una sola vez por sesión del componente.
     */
    async loadDoctors() {
      if (this.doctorsLoaded) return;
      try {
        const res = await doctorService.getDoctors();
        this.doctors = Array.isArray(res) ? res : res.docs || [];
        this.doctorsLoaded = true;
        if (this.doctors.length === 1 && !this.detail.appointment.doctor) {
          this.detail.appointment.doctor = this.doctors[0]._id;
        }
      } catch (err) {
        this.$message.error("No se pudieron cargar los profesionales");
      }
    },

    /**
     * @description Al cambiar de médico se resetea la sede (puede no
     *              pertenecerle la anterior).
     */
    onAppointmentDoctorChange() {
      this.detail.appointment.institution = "";
    },

    /**
     * @description Label del médico en el select: "Apellido, Nombre —
     *              Especialidad".
     * @param {Object} d - User médico con `doctor.medicalSpecialties`
     * @returns {string}
     */
    doctorLabel(d) {
      const name = `${d.lastName || ""}, ${d.firstName || ""}`.trim();
      const specs = ((d.doctor && d.doctor.medicalSpecialties) || [])
        .map((s) => s && s.name)
        .filter(Boolean)
        .join(" / ");
      return specs ? `${name} — ${specs}` : name;
    },

    /**
     * @description Texto del turno populado: "dd/MM/yyyy HH:mm · Dr. X · Sede"
     *              (UC-REG-262, Campana: "dd/MM/yyyy HH:mm · Institución").
     * @param {Object} a - Appointment populado (doctor/institution)
     * @returns {string}
     */
    formatAppointment(a) {
      return formatProcedureAppointment(a, this.formatDate) || "—";
    },

    /**
     * @description Label de estado del turno asociado.
     * @param {string} status
     * @returns {string}
     */
    appointmentStatusLabel(status) {
      const map = {
        pending: "Pendiente",
        admitted: "Admitido",
        started: "En curso",
        done: "Atendido",
        cancelled: "Cancelado",
      };
      return map[status] || status || "";
    },

    /**
     * @description Valida el form de turno y lo convierte al payload del
     *              servicio. Devuelve null si no hay turno que enviar.
     * @returns {{ok:boolean, payload?:Object}}
     */
    buildAppointmentPayload() {
      const form = this.detail.appointment;
      const wants = this.appointmentFormVisible;
      if (!wants) return { ok: true, payload: null };
      if (!form.startsAt) {
        if (this.appointmentRequired) {
          this.$message.warning(
            "Este tipo de trámite requiere cargar fecha y hora del turno"
          );
          return { ok: false };
        }
        // Reprogramar tildado pero sin fecha: se manda sin turno.
        return { ok: true, payload: null };
      }
      const startsAt = new Date(form.startsAt);
      if (Number.isNaN(startsAt.getTime()) || startsAt <= new Date()) {
        this.$message.warning("La fecha y hora del turno debe ser futura");
        return { ok: false };
      }
      if (this.appointmentByInstitution) {
        // UC-REG-262 — institución elegida → prestador ficticio detrás.
        const target = resolveInstitutionAppointment({
          doctors: this.doctors,
          institution: form.institution,
          username: this.procedureDoctorUsername,
        });
        if (target.error) {
          this.$message.warning(target.error);
          return { ok: false };
        }
        return {
          ok: true,
          payload: {
            startsAt: toApiUTC(startsAt),
            doctor: target.doctor,
            institution: target.institution,
          },
        };
      }
      if (!form.doctor) {
        this.$message.warning("Elegí el profesional del turno");
        return { ok: false };
      }
      if (this.selectedDoctorInstitutions.length > 1 && !form.institution) {
        this.$message.warning("Elegí la sede del turno");
        return { ok: false };
      }
      return {
        ok: true,
        payload: {
          startsAt: toApiUTC(startsAt),
          doctor: form.doctor,
          institution: form.institution || undefined,
        },
      };
    },

    async sendMessage() {
      const text = this.detail.newMessage.trim();
      const files = this.detail.newFiles;
      if (!text && files.length === 0) return;
      const { ok, payload } = this.buildAppointmentPayload();
      if (!ok) return;
      this.detail.sending = true;
      try {
        const updated = await procedureService.addMessage(
          this.detail.procedure._id,
          text,
          this.detail.notifyChannel,
          files,
          payload
        );
        this.detail.procedure = updated;
        this.detail.newMessage = "";
        this.detail.newFiles = [];
        this.detail.appointment = this.emptyAppointmentForm();
        if (payload) this.$message.success("Turno asignado al trámite");
        await this.load();
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo enviar el mensaje"
        );
      } finally {
        this.detail.sending = false;
      }
    },

    /**
     * @description UC-REG-300 — Cancela el turno vigente del trámite abierto
     *              (sin reprogramar) con motivo opcional. El backend agrega
     *              el mensaje automático y avisa al vecino por email.
     */
    async cancelDetailAppointment() {
      const procedure = this.detail.procedure;
      if (!procedure) return;
      let value = "";
      try {
        ({ value } = await this.$prompt(
          PROCEDURE_CANCEL_DIALOG.staffMessage,
          PROCEDURE_CANCEL_DIALOG.title,
          {
            confirmButtonText: PROCEDURE_CANCEL_DIALOG.confirm,
            cancelButtonText: PROCEDURE_CANCEL_DIALOG.cancel,
            inputPlaceholder: PROCEDURE_CANCEL_DIALOG.placeholder,
            type: "warning",
          }
        ));
      } catch (e) {
        return;
      }
      this.detail.sending = true;
      try {
        const updated = await procedureService.cancelAppointment(
          procedure._id,
          value
        );
        this.detail.procedure = updated;
        this.detail.appointment = this.emptyAppointmentForm();
        this.$message.success(PROCEDURE_CANCEL_DIALOG.staffSuccess);
        await this.load();
        this.$nextTick(() => this.scrollChatToBottom());
      } catch (err) {
        this.$message.error(procedureCancelErrorMessage(err));
      } finally {
        this.detail.sending = false;
      }
    },

    /**
     * @description Cierra el caso — acción terminal, confirma antes.
     */
    async closeCase() {
      const ok = await this.$confirm(
        "¿Cerrar este trámite? Después no se podrán agregar más mensajes de ninguno de los lados.",
        "Cerrar caso",
        {
          confirmButtonText: "Cerrar caso",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      ).catch(() => false);
      if (!ok) return;
      this.detail.closing = true;
      try {
        const updated = await procedureService.close(this.detail.procedure._id);
        this.detail.procedure = updated;
        await this.load();
        this.$message.success("Trámite cerrado");
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo cerrar el trámite"
        );
      } finally {
        this.detail.closing = false;
      }
    },

    scrollChatToBottom() {
      const el = document.querySelector(".proc-list__chat");
      if (el) el.scrollTop = el.scrollHeight;
    },

    async downloadFile(fileEntry) {
      try {
        const { url } = await procedureService.getFileSignedUrl(
          this.detail.procedure._id,
          fileEntry.key
        );
        window.open(url, "_blank");
      } catch (err) {
        this.$message.error("No se pudo abrir el adjunto");
      }
    },

    formatDate(date) {
      if (!date) return "—";
      try {
        return format(new Date(date), "dd/MM/yyyy HH:mm");
      } catch (e) {
        return "—";
      }
    },

    /**
     * @description UC-REG-297 — "Tipología / Subtipo" del trámite.
     * @param {Object} p - Procedure con procedureType populado
     * @returns {string}
     */
    typeName(p) {
      return typeLabel(p && p.procedureType, this.types) || "—";
    },

    /**
     * @description UC-REG-298 — Primer mensaje del chat: descripción o
     *   médico de preferencia.
     * @param {Object} p
     * @returns {string}
     */
    requestText(p) {
      return requestText(p);
    },

    /**
     * @description UC-REG-299 — Nombre del adjunto con la marca "Derivación".
     * @param {Object} f - ProcedureFile
     * @returns {string}
     */
    fileLabel(f) {
      const kind = fileKindLabel(f);
      const name = (f && (f.originalName || f.key)) || "";
      return kind ? `${kind} · ${name}` : name;
    },

    submitterName(submitter) {
      if (!submitter) return "—";
      const parts = [submitter.firstName, submitter.lastName]
        .filter(Boolean)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());
      return parts.join(" ") || submitter.email || "—";
    },
    submitterTypeLabel(t) {
      if (t === "patient") return "Paciente";
      if (t === "provider") return "Prestador";
      return "—";
    },
    statusLabel(status) {
      if (status === "closed") return "Cerrado";
      if (status === "answered") return "Respondido";
      return "Pendiente";
    },
    statusClass(status) {
      if (status === "closed") return "is-closed";
      if (status === "answered") return "is-answered";
      return "is-pending";
    },

    isStaffMessage(msg) {
      return msg && msg.authorRole === "staff";
    },
  },
};
</script>

<template lang="pug">
.proc-list
  .proc-list__header
    h2.proc-list__title Gestiones de trámites
    button.proc-list__new(@click="openNew") + Nuevo trámite

  .proc-list__filters
    input.proc-list__search(
      type="text"
      v-model="searchInput"
      placeholder="Buscar por descripción o solicitante"
    )
    select.proc-list__filter(v-model="statusFilter")
      option(value="") Todos los estados
      option(value="pending") Pendientes
      option(value="answered") Respondidos
      option(value="closed") Cerrados
    select.proc-list__filter(v-model="typeFilter")
      option(value="") Todos los tipos
      //- UC-REG-297 — tipologías con subtipos agrupadas (filtrar por la
      //- tipología incluye sus subtipos).
      template(v-for="(g, gi) in typeFilterGroups")
        optgroup(v-if="g.label" :key="'g' + gi" :label="g.label")
          option(v-for="o in g.options" :key="o._id" :value="o._id") {{ o.label }}
        template(v-else)
          option(v-for="o in g.options" :key="o._id" :value="o._id") {{ o.label }}
    button.proc-list__clear(v-if="searchInput || statusFilter || typeFilter" @click="clearFilters") Limpiar

  .proc-list__empty(v-if="!loading && procedures.length === 0")
    p No hay trámites que coincidan con estos filtros.

  table.proc-list__table(v-if="procedures.length > 0")
    thead
      tr
        th Fecha
        th Solicitante
        th Rol
        th Tipo
        th Turno
        th Estado
        th
    tbody
      tr(v-for="p in procedures" :key="p._id" @click="openDetail(p)")
        td.proc-list__date {{ formatDate(p.createdAt) }}
        td
          strong {{ submitterName(p.submitter) }}
          .proc-list__submitter-meta(v-if="p.submitter && p.submitter.governmentId && p.submitter.governmentId.number") DNI {{ p.submitter.governmentId.number }}
        td.proc-list__role {{ submitterTypeLabel(p.submitterType) }}
        td.proc-list__type {{ typeName(p) }}
        //- UC-REG-253 — turno vigente a simple vista en la bandeja.
        td.proc-list__appt-cell
          template(v-if="p.appointment && p.appointment.status !== 'cancelled'")
            | {{ formatDate(p.appointment.startsAt) }}
          span.proc-list__appt-missing(v-else-if="p.procedureType && p.procedureType.requiresAppointment && p.submitterType === 'patient'") Pendiente
          template(v-else) —
        td
          span.proc-list__badge(:class="statusClass(p.status)") {{ statusLabel(p.status) }}
        td.proc-list__arrow ›

  //- ═══ Modal detalle + chat + respuesta + cerrar ═══
  .proc-list__modal-backdrop(v-if="detail.visible" @click.self="close")
    .proc-list__modal(v-if="detail.procedure")
      .proc-list__modal-header
        h3 {{ typeName(detail.procedure) }}
        span.proc-list__badge(:class="statusClass(detail.procedure.status)") {{ statusLabel(detail.procedure.status) }}
      .proc-list__view-block
        .proc-list__view-row
          span.proc-list__view-label Solicitante:
          span {{ submitterName(detail.procedure.submitter) }} ({{ submitterTypeLabel(detail.procedure.submitterType) }})
        .proc-list__view-row(v-if="detail.procedure.submitter && detail.procedure.submitter.email")
          span.proc-list__view-label Email:
          span {{ detail.procedure.submitter.email }}
        .proc-list__view-row
          span.proc-list__view-label Enviado:
          span {{ formatDate(detail.procedure.createdAt) }}
        //- UC-REG-316 — qué prioriza el vecino para el turno.
        .proc-list__view-row(v-if="priorityText(detail.procedure)")
          span.proc-list__view-label Prioriza:
          span {{ priorityText(detail.procedure).replace('Prioriza: ', '') }}
        //- UC-REG-298 — médico de preferencia indicado por el vecino.
        .proc-list__view-row(v-else-if="detail.procedure.preferredDoctor")
          span.proc-list__view-label Médico de preferencia:
          span {{ detail.procedure.preferredDoctor }}
        //- UC-REG-253 — turno asociado (vigente o último cancelado/atendido).
        .proc-list__view-row(v-if="detail.procedure.appointment")
          span.proc-list__view-label Turno:
          span
            | {{ formatAppointment(detail.procedure.appointment) }}
            span.proc-list__appt-badge(:class="'is-' + detail.procedure.appointment.status") {{ appointmentStatusLabel(detail.procedure.appointment.status) }}
            //- UC-REG-300 — cancelar sin reprogramar (flag de Campana).
            button.proc-list__appt-cancel(v-if="canCancelDetailAppointment" type="button" @click="cancelDetailAppointment" :disabled="detail.sending || detail.closing") Cancelar turno
        .proc-list__view-row(v-else-if="detailAllowsAppointment")
          span.proc-list__view-label Turno:
          span.proc-list__appt-missing Sin asignar — se carga al responder

      .proc-list__chat
        .proc-list__msg.proc-list__msg--other
          .proc-list__msg-author {{ submitterName(detail.procedure.submitter) }}
          .proc-list__msg-bubble
            .proc-list__msg-text {{ requestText(detail.procedure) }}
            .proc-list__msg-files(v-if="detail.procedure.files && detail.procedure.files.length > 0")
              a.proc-list__msg-file(v-for="(f, i) in detail.procedure.files" :key="i" href="#" @click.prevent="downloadFile(f)")
                | 📎 {{ fileLabel(f) }}
          .proc-list__msg-meta {{ formatDate(detail.procedure.createdAt) }}

        .proc-list__msg(v-for="(m, idx) in detail.procedure.messages" :key="idx" :class="{ 'proc-list__msg--mine': isStaffMessage(m), 'proc-list__msg--other': !isStaffMessage(m) }")
          .proc-list__msg-author(v-if="!isStaffMessage(m)")
            | {{ submitterName(detail.procedure.submitter) }}
          .proc-list__msg-author(v-else)
            | {{ m.author && ((m.author.firstName || '') + ' ' + (m.author.lastName || '')).trim() || 'Centro médico' }}
          .proc-list__msg-bubble
            .proc-list__msg-text(v-if="m.text") {{ m.text }}
            .proc-list__msg-files(v-if="m.files && m.files.length > 0")
              a.proc-list__msg-file(v-for="(f, i) in m.files" :key="i" href="#" @click.prevent="downloadFile(f)")
                | 📎 {{ f.originalName || f.key }}
          .proc-list__msg-meta {{ formatDate(m.createdAt) }}

      .proc-list__compose-block(v-if="detail.procedure.status !== 'closed'")
        //- UC-REG-253 — Turno de la respuesta. Obligatorio si el tipo lo
        //- requiere y no hay turno vigente; opcional (reprogramar) si ya hay.
        .proc-list__appt(v-if="detailAllowsAppointment")
          .proc-list__appt-head
            template(v-if="appointmentRequired")
              strong Turno para esta respuesta *
              span.proc-list__appt-hint Este tipo de trámite se responde asignando un turno. El paciente lo ve en "Mis turnos" y recibe recordatorio el día anterior.
            template(v-else)
              label.proc-list__appt-toggle
                input(type="checkbox" v-model="detail.appointment.reschedule" :disabled="detail.sending || detail.closing")
                span Reprogramar turno
              span.proc-list__appt-hint(v-if="detail.appointment.reschedule") El turno vigente se cancela y se reemplaza por el nuevo.
          .proc-list__appt-form(v-if="appointmentFormVisible")
            .proc-list__appt-field
              label Fecha y hora *
              el-date-picker(
                v-model="detail.appointment.startsAt"
                type="datetime"
                format="dd/MM/yyyy HH:mm"
                placeholder="Elegí fecha y hora"
                :picker-options="appointmentPickerOptions"
                :disabled="detail.sending || detail.closing"
                size="small"
              )
            //- UC-REG-262 (Campana) — se elige la institución; el médico es
            //- un prestador ficticio que no se muestra.
            .proc-list__appt-field(v-if="appointmentByInstitution")
              label Institución *
              select(v-model="detail.appointment.institution" :disabled="detail.sending || detail.closing")
                option(value="" disabled) Elegí una institución
                option(v-for="i in procedureInstitutions" :key="i._id" :value="i._id") {{ i.name }}
              span.proc-list__appt-hint(v-if="doctorsLoaded && procedureInstitutions.length === 0") No hay instituciones configuradas para turnos de trámite. Avisale al administrador.
            template(v-else)
              .proc-list__appt-field
                label Profesional *
                select(v-model="detail.appointment.doctor" @change="onAppointmentDoctorChange" :disabled="detail.sending || detail.closing")
                  option(value="" disabled) Elegí un profesional
                  option(v-for="d in doctors" :key="d._id" :value="d._id") {{ doctorLabel(d) }}
                span.proc-list__appt-hint(v-if="doctorsLoaded && doctors.length === 0") No hay profesionales con sede y especialidad cargadas. Cargá uno desde Prestadores.
              .proc-list__appt-field(v-if="selectedDoctorInstitutions.length > 1")
                label Sede *
                select(v-model="detail.appointment.institution" :disabled="detail.sending || detail.closing")
                  option(value="" disabled) Elegí una sede
                  option(v-for="i in selectedDoctorInstitutions" :key="i._id" :value="i._id") {{ i.name }}
        .proc-list__compose
          textarea(
            v-model="detail.newMessage"
            rows="3"
            maxlength="4000"
            placeholder="Escribí una respuesta para el solicitante…"
            :disabled="detail.sending || detail.closing"
          )
          button.proc-list__btn.proc-list__btn--primary(@click="sendMessage" :disabled="detail.sending || detail.closing || (!detail.newMessage.trim() && detail.newFiles.length === 0)")
            | {{ detail.sending ? "Enviando…" : "Enviar respuesta" }}
        //- Fila de adjuntos: botón + chips de archivos ya elegidos.
        .proc-list__attach-row
          label.proc-list__attach-btn(:class="{ 'is-disabled': detail.sending || detail.closing }")
            micon(name="attach_file")
            span Adjuntar
            input(type="file" multiple @change="onPickChatFiles" :disabled="detail.sending || detail.closing" hidden)
          .proc-list__attach-chips(v-if="detail.newFiles.length > 0")
            .proc-list__attach-chip(v-for="(f, i) in detail.newFiles" :key="i")
              span 📎 {{ f.name }} ({{ formatFileSize(f.size) }})
              button(type="button" @click="removeChatFile(i)" :disabled="detail.sending || detail.closing") ×
        //- Selector de canal — evita spamear ambos. Default email.
        .proc-list__notify
          span.proc-list__notify-label Notificar por:
          label.proc-list__notify-opt
            input(type="radio" value="email" v-model="detail.notifyChannel")
            span Email
          label.proc-list__notify-opt
            input(type="radio" value="whatsapp" v-model="detail.notifyChannel")
            span WhatsApp
          label.proc-list__notify-opt
            input(type="radio" value="none" v-model="detail.notifyChannel")
            span No notificar
      .proc-list__closed-note(v-else)
        | Este trámite fue cerrado el {{ formatDate(detail.procedure.closedAt) }}
        template(v-if="detail.procedure.closedBy")
          |  por {{ detail.procedure.closedBy.firstName }} {{ detail.procedure.closedBy.lastName }}
        | .

      .proc-list__modal-actions
        button.proc-list__btn.proc-list__btn--danger(v-if="detail.procedure.status !== 'closed'" @click="closeCase" :disabled="detail.sending || detail.closing")
          | {{ detail.closing ? "Cerrando…" : "Cerrar caso" }}
        button.proc-list__btn.proc-list__btn--secondary(@click="close") Cerrar ventana

  //- ═══ Modal Nuevo trámite (admin en nombre de un paciente) ═══
  .proc-list__modal-backdrop(v-if="newProc.visible" @click.self="closeNew")
    .proc-list__modal
      .proc-list__modal-header
        h3 Nuevo trámite
      .proc-list__field
        label Paciente *
        input.proc-list__patient-search(
          type="text"
          v-model="newProc.patientQuery"
          placeholder="Buscá por nombre, apellido o DNI"
          @input="onPatientQueryInput"
        )
        .proc-list__patient-results(v-if="newProc.patientResults.length > 0 && !newProc.form.submitterPatient")
          .proc-list__patient-result(v-for="p in newProc.patientResults" :key="p.userId" @click="pickPatient(p)")
            strong {{ p.firstName }} {{ p.lastName }}
            span(v-if="p.governmentId && p.governmentId.number")  · DNI {{ p.governmentId.number }}
            .proc-list__patient-contact
              span(v-if="p.email") 📧 {{ p.email }}
              span(v-if="p.phone" style="margin-left: 8px;") 📱 {{ p.phone }}
              span(v-if="!p.email && !p.phone" style="color: #d97706;") Sin contacto cargado
        .proc-list__patient-hint(v-if="newProc.patientSearching") Buscando…
        .proc-list__patient-hint(v-else-if="newProc.patientQuery.length >= 2 && newProc.patientResults.length === 0 && !newProc.form.submitterPatient") Sin resultados
        //- Muestra identidad completa del paciente elegido (útil para
        //- confirmar antes de crear y ver qué canales de contacto tiene).
        .proc-list__patient-picked(v-if="newProc.form.submitterPatient")
          span(v-if="newProc.form.submitterPatient.email") 📧 {{ newProc.form.submitterPatient.email }}
          span(v-if="newProc.form.submitterPatient.phone" style="margin-left: 10px;") 📱 {{ newProc.form.submitterPatient.phone }}
          span(v-if="!newProc.form.submitterPatient.email && !newProc.form.submitterPatient.phone" style="color: #d97706;") ⚠ Este paciente no tiene email ni teléfono cargados. No podrá recibir notificación.
      .proc-list__field
        label Tipo *
        select(v-model="newProc.form.procedureType" @change="onNewProcTypeChange")
          option(value="" disabled) Elegí un tipo
          option(v-for="t in newProcTopTypes" :key="t._id" :value="t._id") {{ t.name }}
      //- UC-REG-297 — subtipología.
      .proc-list__field(v-if="newProcSubtypes.length > 0")
        label Subtipo *
        select(v-model="newProc.form.procedureSubtype")
          option(value="" disabled) Elegí un subtipo
          option(v-for="t in newProcSubtypes" :key="t._id" :value="t._id") {{ t.name }}
      //- UC-REG-298 (Campana) — médico de preferencia en lugar de la descripción.
      .proc-list__field(v-if="preferredDoctorMode")
        label Médico de preferencia (opcional)
        input(type="text" v-model="newProc.form.preferredDoctor" :maxlength="preferredDoctorMax" placeholder="Profesional de cabecera que pide el vecino")
      .proc-list__field(v-else)
        label Descripción *
        textarea(v-model="newProc.form.description" rows="4" maxlength="2000" placeholder="Motivo del trámite, datos relevantes…")
      .proc-list__field
        label Notificar al paciente por
        .proc-list__notify(style="margin-top: 0;")
          label.proc-list__notify-opt
            input(type="radio" value="email" v-model="newProc.notifyChannel")
            span Email
          label.proc-list__notify-opt
            input(type="radio" value="whatsapp" v-model="newProc.notifyChannel")
            span WhatsApp
          label.proc-list__notify-opt
            input(type="radio" value="none" v-model="newProc.notifyChannel")
            span No notificar
      .proc-list__field
        label Adjuntos (opcional)
        .proc-list__attach-row
          label.proc-list__attach-btn
            micon(name="attach_file")
            span Elegir archivos
            input(type="file" multiple @change="onNewProcFiles" hidden)
          .proc-list__attach-chips(v-if="newProc.form.files.length > 0")
            .proc-list__attach-chip(v-for="(f, i) in newProc.form.files" :key="i")
              span 📎 {{ f.name }} ({{ formatFileSize(f.size) }})
              button(type="button" @click="removeNewProcFile(i)") ×
      .proc-list__modal-actions
        button.proc-list__btn.proc-list__btn--secondary(@click="closeNew" :disabled="newProc.saving") Cancelar
        button.proc-list__btn.proc-list__btn--primary(@click="submitNewProc" :disabled="newProc.saving")
          | {{ newProc.saving ? "Creando…" : "Crear trámite" }}
</template>

<style lang="scss" scoped>
.proc-list {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}
.proc-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.proc-list__title {
  margin: 0;
  font-size: 20px;
  color: $navy;
}
.proc-list__new {
  background: $navy;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: darken($navy, 5%);
  }
}
.proc-list__field {
  margin-bottom: 14px;
  label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 4px;
  }
  select,
  textarea,
  input[type="text"] {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    background: white;
    &:focus {
      outline: none;
      border-color: $navy;
    }
  }
  textarea {
    resize: vertical;
  }
}
.proc-list__patient-search {
  width: 100%;
}
.proc-list__patient-results {
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
}
.proc-list__patient-result {
  padding: 8px 10px;
  cursor: pointer;
  font-size: 13px;
  color: $navy;
  border-top: 1px solid #f1f5f9;
  &:hover {
    background: #f8fafc;
  }
  &:first-child {
    border-top: none;
  }
}
.proc-list__patient-hint {
  font-size: 11px;
  color: #64748b;
  margin-top: 4px;
}
.proc-list__patient-contact {
  margin-top: 3px;
  font-size: 11px;
  color: #64748b;
}
.proc-list__patient-picked {
  margin-top: 6px;
  font-size: 12px;
  color: #334155;
  padding: 6px 10px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid $navy;
}
.proc-list__attach-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 6px;
}
.proc-list__attach-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  color: #334155;
  background: white;
  cursor: pointer;
  &:hover {
    border-color: $navy;
    color: $navy;
  }
  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.proc-list__attach-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.proc-list__attach-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #f1f5f9;
  border-radius: 12px;
  font-size: 11px;
  color: #334155;
  button {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 0 2px;
    &:hover {
      color: #dc2626;
    }
  }
}
.proc-list__filters {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;
}
.proc-list__search {
  flex: 1;
  min-width: 220px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  &:focus {
    outline: none;
    border-color: $navy;
  }
}
.proc-list__filter {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  min-width: 160px;
}
.proc-list__clear {
  background: none;
  border: none;
  color: $navy;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 8px;
  &:hover {
    text-decoration: underline;
  }
}
.proc-list__empty {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 13px;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
}
.proc-list__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  thead th {
    text-align: left;
    padding: 12px;
    color: #64748b;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  tbody td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
  }
  tbody tr {
    cursor: pointer;
    transition: background 0.12s;
    &:hover {
      background: #f8fafc;
    }
    &:last-child td {
      border-bottom: none;
    }
  }
}
.proc-list__date {
  color: #64748b;
  white-space: nowrap;
  font-size: 12px;
}
.proc-list__submitter-meta {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}
.proc-list__role {
  font-size: 12px;
  color: #64748b;
}
.proc-list__type {
  color: $navy;
  font-weight: 600;
}
.proc-list__arrow {
  color: #cbd5e1;
  text-align: right;
  width: 30px;
  font-size: 18px;
}
.proc-list__badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  &.is-pending {
    background: rgba(217, 119, 6, 0.12);
    color: #d97706;
  }
  &.is-answered {
    background: rgba(22, 163, 74, 0.12);
    color: #16a34a;
  }
  &.is-closed {
    background: #f1f5f9;
    color: #64748b;
  }
}
.proc-list__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.proc-list__modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 680px;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}
.proc-list__modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  h3 {
    margin: 0;
    font-size: 16px;
    color: $navy;
  }
}
.proc-list__view-block {
  border-top: 1px solid #f1f5f9;
  padding-top: 12px;
  margin-bottom: 14px;
}
.proc-list__view-row {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 3px 0;
  font-size: 13px;
}
.proc-list__view-label {
  font-weight: 700;
  color: #334155;
  min-width: 100px;
}

/* Chat */
.proc-list__chat {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 12px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.proc-list__msg {
  display: flex;
  flex-direction: column;
  max-width: 78%;
  &--mine {
    align-self: flex-end;
    align-items: flex-end;
    .proc-list__msg-bubble {
      background: $navy;
      color: white;
    }
    .proc-list__msg-file {
      color: white;
      text-decoration: underline;
    }
  }
  &--other {
    align-self: flex-start;
    align-items: flex-start;
    .proc-list__msg-bubble {
      background: white;
      color: $navy;
      border: 1px solid #e2e8f0;
    }
  }
}
.proc-list__msg-author {
  font-size: 10.5px;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 3px;
  padding: 0 4px;
}
.proc-list__msg-bubble {
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.4;
}
.proc-list__msg-text {
  white-space: pre-wrap;
  word-wrap: break-word;
}
.proc-list__msg-files {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.proc-list__msg-file {
  font-size: 12px;
  color: $navy;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
.proc-list__msg-meta {
  font-size: 10px;
  color: #64748b;
  margin-top: 3px;
  padding: 0 4px;
}
.proc-list__compose-block {
  margin-bottom: 12px;
}
.proc-list__compose {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  textarea {
    flex: 1;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    &:focus {
      outline: none;
      border-color: $navy;
    }
  }
  button {
    height: 42px;
    padding: 0 18px;
  }
}
.proc-list__notify {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-top: 8px;
  padding: 6px 2px 0;
  font-size: 12px;
  color: #334155;
}
.proc-list__notify-label {
  color: #64748b;
  font-weight: 700;
}
.proc-list__notify-opt {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  input {
    margin: 0;
    cursor: pointer;
  }
}
.proc-list__closed-note {
  text-align: center;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}
.proc-list__modal-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 4px;
}
.proc-list__btn {
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &--primary {
    background: $navy;
    color: white;
    &:hover:not(:disabled) {
      background: darken($navy, 5%);
    }
  }
  &--secondary {
    background: #f1f5f9;
    color: #334155;
    &:hover:not(:disabled) {
      background: #e2e8f0;
    }
  }
  &--danger {
    background: #dc2626;
    color: white;
    &:hover:not(:disabled) {
      background: darken(#dc2626, 5%);
    }
  }
}

/* ═══ UC-REG-253 — turno asociado al trámite ═══ */
.proc-list__appt-cell {
  white-space: nowrap;
  font-size: 12px;
  color: #0f172a;
}
.proc-list__appt-missing {
  color: #d97706;
  font-size: 12px;
  font-weight: 700;
}
/* UC-REG-300 — cancelar el turno del trámite desde el detalle */
.proc-list__appt-cancel {
  margin-left: 10px;
  background: #fee2e2;
  color: #b91c1c;
  border: none;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:hover:not(:disabled) {
    background: #fecaca;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.proc-list__appt-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: #f1f5f9;
  color: #64748b;
  &.is-pending,
  &.is-admitted,
  &.is-started {
    background: rgba(37, 99, 235, 0.1);
    color: #1d4ed8;
  }
  &.is-done {
    background: rgba(22, 163, 74, 0.12);
    color: #16a34a;
  }
  &.is-cancelled {
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
  }
}
.proc-list__appt {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.proc-list__appt-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #1e3a8a;
}
.proc-list__appt-hint {
  font-size: 11px;
  color: #475569;
  line-height: 1.4;
}
.proc-list__appt-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  cursor: pointer;
  input {
    margin: 0;
  }
}
.proc-list__appt-form {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
}
.proc-list__appt-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
  flex: 1;
  label {
    font-size: 11px;
    font-weight: 700;
    color: #334155;
  }
  select {
    padding: 7px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    background: white;
    &:focus {
      outline: none;
      border-color: $teal;
    }
  }
  .el-date-editor {
    width: 100%;
  }
}
</style>
